import { to12Hour } from '../../../../utils/birthInfo';
import { MONTHS } from '../../../common/BirthDatePicker';

/**
 * Pure helpers for `BirthChartFlow` (HORO-10360). Kept out of the component so
 * the step/format rules are unit-testable without rendering.
 *
 * `birthDate` is a plain 'YYYY-MM-DD' string and is never run through `Date`:
 * a Date round-trip is exactly how the date shifts a day for viewers west of
 * UTC (see utils/birthInfo.js).
 */
const pad = (n) => String(n).padStart(2, '0');

export const isTimeMissing = (details) => !details.birthTime && details.birthTimeKnown !== false;

/** The first step whose data is missing, or verify when everything is there. */
export const firstMissingStep = (details) => {
    if (!details.birthDate) return 'date';
    if (isTimeMissing(details)) return 'time';
    if (!details.place) return 'place';
    return 'verify';
};

/**
 * Flow details with the gaps filled from the profile's stored details: whatever
 * the flow already has wins, the profile only supplies what is still empty.
 */
export const prefillDetails = (current, stored) => {
    // A time taken from the profile goes with the profile's date: the two are one
    // stored instant, so mixing in a date from elsewhere (the server's CTA details
    // read that instant in UTC) could shift the date a day away from its time.
    const takeStoredTime = !current.birthTime && current.birthTimeKnown !== false && !!stored.birthTime;
    return {
        birthDate: (takeStoredTime && stored.birthDate) || current.birthDate || stored.birthDate || '',
        birthTime: current.birthTime || (current.birthTimeKnown === false ? null : stored.birthTime) || null,
        birthTimeKnown: current.birthTimeKnown ?? stored.birthTimeKnown ?? null,
        place: current.place || stored.place || null
    };
};

/** 'YYYY-MM-DD' → { year, month (1-12), day }, or null. */
export const parseBirthDate = (birthDate) => {
    if (!birthDate) return null;
    const [year, month, day] = birthDate.split('-').map(Number);
    if (!year || !month || !day) return null;
    return { year, month, day };
};

export const toBirthDateString = ({ year, month, day }) => `${year}-${pad(month)}-${pad(day)}`;

/** 'March 8, 2003' */
export const formatBirthDate = (birthDate) => {
    const parts = parseBirthDate(birthDate);
    if (!parts) return '';
    return `${MONTHS[parts.month - 1]} ${parts.day}, ${parts.year}`;
};

/** '12:03 PM' from 'HH:mm', or '' */
export const formatBirthTime = (birthTime) => {
    if (!birthTime) return '';
    const [hour, minute] = birthTime.split(':').map(Number);
    const { hour: h, minute: m, meridiem } = to12Hour(hour, minute);
    return `${h}:${pad(m)} ${meridiem}`;
};

/** First comma-separated segment of a Places description: 'San Francisco, CA, USA' → 'San Francisco'. */
export const cityFromPlace = (place) => {
    const description = place?.description || '';
    return description.split(',')[0].trim();
};

/** Wheel positions for the time sheet: the stored time, or noon. */
export const timeToWheel = (birthTime) => {
    if (!birthTime) return { hour: 12, minute: 0, meridiem: 'PM' };
    const [hour, minute] = birthTime.split(':').map(Number);
    return to12Hour(hour, minute);
};
